import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Users, Briefcase, FileText, TrendingUp, Calculator, BarChart3, GraduationCap, ShieldCheck, CheckCircle2, Zap, LayoutDashboard } from 'lucide-react';

const AboutPage: React.FC = () => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);

  // 1. Скролл вверх при открытии страницы
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // 2. Параллакс эффект (как на главной)
  const handleScroll = useCallback(() => {
    requestAnimationFrame(() => setScrollY(window.scrollY));
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const modules = [
    { name: 'HRM (Кадры)', icon: <Users size={32} />, color: '#2575fc', desc: 'Сотрудники, отпуска, приказы, ЭЦП, воинский учет, кадровый резерв.' },
    { name: 'Рекрутинг', icon: <Briefcase size={32} />, color: '#6a11cb', desc: 'Вакансии, кандидаты, воронка найма, тесты, адаптация и онбординг.' },
    { name: 'КДП (Документы)', icon: <FileText size={32} />, color: '#28a745', desc: 'Приказы, ЭДО, шаблоны, архив, массовые рассылки.' },
    { name: 'SalesCRM', icon: <TrendingUp size={32} />, color: '#e83e8c', desc: 'Воронки продаж, сделки, задачи, документы, встречи.' },
    { name: 'Бухгалтерия', icon: <Calculator size={32} />, color: '#ffc107', desc: 'Счета, контрагенты, платежи, интеграция с 1С.' },
    { name: 'Аналитика (BI)', icon: <BarChart3 size={32} />, color: '#17a2b8', desc: 'BI-дашборды, KPI, HR-метрики, отчёты, прогноз найма.' },
    { name: 'Обучение (ELearning)', icon: <GraduationCap size={32} />, color: '#8b5cf6', desc: 'Курсы, тесты, база знаний, развитие сотрудников.' },
    { name: 'Безопасность', icon: <ShieldCheck size={32} />, color: '#ef4444', desc: 'Хранение данных по 152-ФЗ, роли, аудит действий.' },
  ];

  const features = [
    'Единая экосистема: 8+ модулей в одном окне',
    'Автоматизация рутины: отпуска, приказы, документы',
    'Воронка найма и база кандидатов с тестами',
    'HR-аналитика и прогноз найма',
    'Соответствие 152-ФЗ и защита данных',
    'Бесплатный тестовый период 5 дней',
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #e6f0fa 100%)', fontFamily: 'Inter, sans-serif', position: 'relative', overflowX: 'hidden' }}>
      
      {/* Параллакс-пятна на фоне (как на главной) */}
      <div style={{ position: 'fixed', borderRadius: '50%', zIndex: 0, pointerEvents: 'none', willChange: 'transform', transform: `translate3d(0, ${scrollY * 0.15}px, 0)`, background: 'radial-gradient(circle, rgba(106,17,203,0.15) 0%, transparent 70%)', top: '-10%', left: '-5%', width: '40vw', height: '40vw' }} />
      <div style={{ position: 'fixed', borderRadius: '50%', zIndex: 0, pointerEvents: 'none', willChange: 'transform', transform: `translate3d(0, ${scrollY * -0.25}px, 0)`, background: 'radial-gradient(circle, rgba(37,117,252,0.12) 0%, transparent 70%)', bottom: '10%', right: '0%', width: '50vw', height: '50vw' }} />

      {/* Шапка (стеклянная, как на главной) */}
      <header style={{ padding: '10px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(10px)', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 1px 10px rgba(0,0,0,0.05)', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: '#64748b', fontWeight: 600, fontSize: 14, padding: '8px 16px', borderRadius: 20, background: 'rgba(0,0,0,0.03)', transition: 'background 0.2s' }}>
            <ArrowLeft size={18} /> На главную
          </Link>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img src="/images/14.png" alt="Лама" style={{ height: 40, width: 'auto' }} />
        </div>
        
      </header>

      {/* Основной контент */}
      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '60px 20px', position: 'relative', zIndex: 1 }}>
        
        {/* Заголовок (Hero) */}
        <div style={{ textAlign: 'center', marginBottom: 60, maxWidth: 800, margin: '0 auto 60px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(106,17,203,0.1)', padding: '6px 16px', borderRadius: 30, fontSize: 14, color: '#6a11cb', fontWeight: 500, marginBottom: 24, boxShadow: '0 4px 15px rgba(106,17,203,0.15)' }}>
            <Zap size={16} /><span>Единая HR-экосистема</span>
          </div>
          <h1 style={{ fontSize: 42, fontWeight: 800, color: '#1a1a2e', marginBottom: 20, lineHeight: 1.15, letterSpacing: '-0.02em' }}>
            О платформе <span style={{ background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>HR LAMA</span>
          </h1>
          <p style={{ fontSize: 18, color: '#4a4a68', lineHeight: 1.6 }}>
            Российская HR-экосистема для работы с людьми и деньгами. Кадры, найм, документы, сделки, финансы и обучение — в едином пространстве.
          </p>
        </div>

        {/* Текстовый блок (Стекло) */}
        <div style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(10px)', borderRadius: 32, padding: 40, marginBottom: 60, border: '1px solid rgba(255,255,255,0.3)', boxShadow: '0 15px 35px rgba(0,0,0,0.08)' }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1a1a2e', marginBottom: 20 }}>Что такое HR LAMA?</h2>
          <p style={{ fontSize: 16, color: '#4a4a68', lineHeight: 1.8, marginBottom: 20 }}>
            HR LAMA — это облачная HRM-система нового поколения для управления персоналом, кадрового учета и автоматизации HR-процессов. Платформа объединяет рекрутинг, подбор персонала, воронку найма, базу кандидатов, вакансии и адаптацию новых сотрудников.
          </p>
          <p style={{ fontSize: 16, color: '#4a4a68', lineHeight: 1.8, marginBottom: 20 }}>
            Встроенные инструменты HR-аналитики, прогноза найма, KPI-конструктора, психологических тестов и оценки персонала делают HR LAMA идеальным выбором для малого и среднего бизнеса. Мы обеспечиваем безопасное хранение данных в соответствии с 152-ФЗ, предлагаем прозрачные тарифы и бесплатный тестовый период.
          </p>
          <p style={{ fontSize: 16, color: '#4a4a68', lineHeight: 1.8 }}>
            С HR LAMA вы получаете единую экосистему для управления кадрами, продажами, документами и обучением — без хаоса в таблицах и разрозненных сервисах.
          </p>
        </div>

        {/* Сетка модулей */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <h2 style={{ fontSize: 32, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>8 рабочих контуров — в одной системе</h2>
          <p style={{ fontSize: 18, color: '#666' }}>Каждый модуль — законченный инструмент. Вместе — единая среда.</p>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 24, marginBottom: 80 }}>
          {modules.map((m, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(10px)', padding: 24, borderRadius: 24, boxShadow: '0 15px 30px rgba(0,0,0,0.08)', border: '1px solid rgba(255,255,255,0.3)', display: 'flex', flexDirection: 'column', transition: 'transform 0.2s, box-shadow 0.2s' }}>
              <div style={{ width: 56, height: 56, borderRadius: 16, background: m.color + '15', color: m.color, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                {m.icon}
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 600, color: '#1a1a2e', marginBottom: 12 }}>{m.name}</h3>
              <p style={{ fontSize: 14, color: '#666', lineHeight: 1.5, margin: 0, flex: 1 }}>{m.desc}</p>
            </div>
          ))}
        </div>

        {/* Преимущества (Градиентный блок) */}
        <div style={{ background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', borderRadius: 40, padding: '60px 40px', color: '#fff', textAlign: 'center', boxShadow: '0 30px 60px rgba(106,17,203,0.4)', marginBottom: 60 }}>
          <h2 style={{ fontSize: 32, fontWeight: 700, marginBottom: 40 }}>Почему выбирают HR LAMA?</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20, textAlign: 'left', maxWidth: 800, margin: '0 auto 40px' }}>
            {features.map((text, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, fontSize: 16 }}>
                <CheckCircle2 size={24} color="#fff" style={{ flexShrink: 0 }} />
                <span>{text}</span>
              </div>
            ))}
          </div>
          <button onClick={() => navigate('/register')} style={{ background: '#fff', color: '#2575fc', border: 'none', padding: '16px 40px', borderRadius: 40, fontSize: 18, fontWeight: 700, cursor: 'pointer', boxShadow: '0 10px 20px rgba(0,0,0,0.2)', display: 'inline-flex', alignItems: 'center', gap: 8, transition: 'transform 0.2s' }}>
            Попробовать бесплатно <ArrowLeft size={20} style={{ transform: 'rotate(180deg)' }} />
          </button>
        </div>

        {/* SEO-текст */}
        <div style={{ background: 'rgba(255,255,255,0.6)', borderRadius: 24, padding: 32, border: '1px solid rgba(255,255,255,0.8)' }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>HR LAMA — российская HRM-система для управления персоналом</h2>
          <p style={{ fontSize: 14, color: '#4a4a68', lineHeight: 1.6, margin: 0 }}>
            HR LAMA — это современная облачная HRM-система для управления персоналом, кадрового учета и автоматизации HR-процессов. Платформа предлагает рекрутинг, подбор персонала, воронку найма, базу кандидатов и вакансий, а также модуль ELearning для обучения и развития сотрудников. Встроенные инструменты HR-аналитики, прогноза найма, KPI-конструктор, психологические тесты и оценка персонала, управление кадровым резервом и воинским учетом делают HR LAMA идеальным выбором для малого и среднего бизнеса. Ознакомьтесь с тарифами HR и начните бесплатно.
          </p>
        </div>

      </main>
    </div>
  );
};

export default AboutPage;
